import type { TranslationDict } from '@fluxora/i18n';

/**
 * The `shop` i18n namespace (en-US). Keys are the dotted remainder after the
 * `shop.` namespace prefix, matching `@fluxora/i18n`'s `t('shop.<key>')` lookup.
 * No user-facing string is hardcoded in a component — every one resolves here.
 */
export const shopEn: TranslationDict = {
  // ── App shell — header / trust strip / footer (Phase 1 storefront UI) ─────
  // The brand name + support number are storefront chrome. The phone number and
  // the PUCT REP licence number are CLEARLY-MARKED PLACEHOLDERS — never present
  // them as real regulatory identifiers until real values are configured.
  'brand.name': 'Rheora',
  // Unused since the tagline was removed from the header — kept so the string is not
  // lost if a tagline is wanted back.
  'brand.tagline': 'Texas electricity, priced the honest way',
  'header.home.aria': 'Rheora storefront home',
  'header.support.label': 'Questions?',
  'header.support.phone': '1-800-000-0000',
  'header.support.phoneNote': '(placeholder)',
  'header.support.aria': 'Call storefront support (placeholder number)',
  'header.cta.shop': 'Shop plans',

  // Floating pill nav — primary storefront navigation. "Plans" is a real route;
  // "How it works" / "Support" are in-page anchors on the landing page.
  'nav.plans': 'Plans',
  'nav.how': 'How it works',
  'nav.support': 'Support',

  // Trust strip — factual, regulator-anchored claims only. NO star ratings and
  // NO review counts are asserted here; those need real data (left as a clean
  // slot, see `trust.ratings.slot`).
  'trust.aria': 'Why shop with us',
  'trust.puct': 'PUCT-regulated',
  'trust.secure': 'Secure checkout',
  'trust.noHiddenFees': 'No hidden fees',
  'trust.satisfaction': 'Satisfaction promise',
  'trust.ratings.slot': 'Customer reviews coming soon',

  // Footer
  'footer.docs.heading': 'Plan documents',
  'footer.docs.efl': 'Electricity Facts Label (EFL)',
  'footer.docs.tos': 'Terms of Service (TOS)',
  'footer.docs.yrac': 'Your Rights as a Customer (YRAC)',
  // ⚠️ These three are NOT links, deliberately (E-SF19). They are per-PLAN
  // documents — §25.475 requires the EFL for the specific product being sold — so
  // there is no site-wide copy to link to, and all three previously pointed at "/".
  // This line says where they actually are instead of manufacturing a destination.
  'footer.docs.whereToFind':
    'Shown on each plan’s page, and again with your contract before you sign.',
  'footer.compliance.heading': 'Compliance',
  // ⚠️ RENDERED ONLY WHEN A REAL CERTIFICATE NUMBER IS CONFIGURED. This key used to
  // hold the literal "PUCT REP License #XXXXX" with a "placeholder" caption beneath
  // it; a fabricated regulatory identifier is a false statement about our
  // authorisation, not a placeholder. See `env.puctRepLicense`.
  'footer.puctLicense': 'PUCT REP Certificate #{{number}}',
  // ── Footer "Contact us" (E-CRM03) — writes to the CRM operator queue ───
  'footer.contactUs.title': 'Contact us',
  'footer.contactUs.name': 'Your name',
  'footer.contactUs.email': 'Your email',
  'footer.contactUs.message': 'How can we help?',
  'footer.contactUs.cta': 'Send message',
  'footer.contactUs.thanks': "Thanks — your message is with our team. We'll reply to your email.",
  'footer.contactUs.failed': "Your message didn't send. Please try again.",
  'footer.secure': 'Secure, encrypted checkout. Your details are protected in transit.',
  'footer.copyright': '© {{year}} Rheora. All rights reserved.',

  // Funnel progress stepper — actual route order:
  // Plan → Service address → Approval (credit) → Estimate → Sign.
  'funnel.aria': 'Enrollment progress',
  'funnel.step.plan': 'Plan',
  'funnel.step.address': 'Service address',
  'funnel.step.credit': 'Approval',
  'funnel.step.estimate': 'Estimate',
  'funnel.step.sign': 'Sign',
  'funnel.state.done': ' — complete',
  'funnel.state.active': ' — current step',
  'funnel.state.pending': ' — upcoming',

  // Funnel REHYDRATE (GET /bff/funnel) — the one blocking read on storefront mount,
  // which is what lets a browser refresh keep the shopper's progress. Copy is a
  // neutral "restoring", never a promise about WHAT will be restored: a first-time
  // visitor sees this too, and for them there is nothing to restore.
  'funnel.hydrating': 'Restoring your enrollment…',

  // Shelf page
  'shelf.title': 'Shop electricity plans',
  'shelf.subtitle':
    'The headline figure is each plan’s PUCT average price at your usage, not the energy rate alone.',
  'shelf.loading': 'Loading plans…',
  'shelf.error.title': "We couldn't load plans",
  'shelf.error.body': 'Something went wrong reaching the plan catalogue. Please try again.',
  'shelf.empty.title': 'No plans available',
  'shelf.empty.body': 'There are no shoppable plans right now. Please check back soon.',
  'shelf.retry': 'Try again',
  'shelf.usageContext': 'Showing average price at {{kwh}} kWh/mo',
  'shelf.changeUsage': 'Change usage',
  // On-shelf usage toggle (replaces the passive "change usage" bar)
  'shelf.usage.heading': 'Compare at your usage',
  'shelf.usage.aria': 'Choose a monthly usage level',
  'shelf.usage.option': '{{kwh}} kWh/mo',
  'shelf.retakeQuiz': 'Retake quiz',
  'shelf.loading.aria': 'Loading plans',

  // Plan card
  'card.avgPrice.label': 'Average price',
  'card.avgPrice.unit': '¢/kWh',
  'card.avgPrice.at': 'at {{kwh}} kWh/mo',
  'card.avgPrice.loading': 'Calculating…',
  // ⚠️ THE ERROR STATE, AND ONLY THE ERROR STATE. This string used to cover a
  // deliberate §25.475 decision too, which is how a customer came to see "Price
  // unavailable" on a plan an agent had just offered them. Anything with a
  // reason belongs on a `withheld.*` key below; leave this one meaning exactly
  // "we could not get it, and a retry may work".
  'card.avgPrice.unavailable': 'Price unavailable',
  // ── No average price is PUBLISHED for this plan ─────────────────────────
  // Says what WE do and why, and points at the plan's own documents for the
  // terms. It deliberately makes NO claim about the plan being cheap, dear or
  // otherwise: the reason this copy exists is that we cannot compute the figure
  // that would support any such claim, so we must not imply one either way.
  'card.avgPrice.withheld.headline': 'No average price published',
  // ⚠️ ONE STRING, TWO SURFACES. `PlanPriceHeadline` renders on the shelf card
  // AND on the plan-detail page, so the last sentence must be true in both
  // places: "Open the plan for…" reads as a broken instruction to someone who is
  // already on the plan page. "See the plan's …" points forward from the card and
  // points down the page on the detail view. Keep it location-neutral, or give
  // the component a surface prop — do not let it become subtly wrong on one.
  'card.avgPrice.withheld.promotionalTerm':
    'This plan includes a promotional term whose value depends on how much you use, so no one average figure would describe it honestly. See the plan’s charges, fees and Electricity Facts Label for its full terms.',
  'card.term': '{{months}}-month term',
  'card.term.none': 'No contract term',
  'card.renewable': '{{pct}} renewable',
  'card.tdsp': 'Delivery: {{tdsp}}',
  'card.provider': 'Provider: {{name}}',
  'card.etf': 'Cancel fee {{amount}}',
  'card.etf.none': 'No cancel fee',
  'card.viewDetail': 'View plan',
  'pricePoints.explainer':
    'The PUCT average price folds in delivery charges and fixed fees, then divides by usage — so it falls as you use more.',

  // Needs quiz (AC3)
  'quiz.title': 'Find your fit',
  'quiz.subtitle': 'Two quick questions so we can show the price at your usage.',
  'quiz.usage.label': 'How big is your home?',
  'quiz.usage.small': 'Small — apartment, 1–2 people',
  'quiz.usage.medium': 'Medium — typical house',
  'quiz.usage.large': 'Large — big house, high usage',
  'quiz.home.label': 'What type of home?',
  'quiz.home.apartment': 'Apartment',
  'quiz.home.house': 'House',
  'quiz.home.condo': 'Condo',
  'quiz.submit': 'Show my plans',
  'quiz.skip': 'Skip for now',

  // Plan detail page
  'detail.back': 'Back to plans',
  'detail.loading': 'Loading plan…',
  'detail.error.title': "We couldn't load this plan",
  'detail.error.body': 'Something went wrong. Please try again.',
  'detail.notFound.title': 'Plan not found',
  'detail.notFound.body': 'This plan is no longer available.',
  'detail.energyChargeHeading': 'Energy charge',
  'detail.energyChargeNote': 'The energy charge alone is not the price you pay.',
  'detail.term': 'Contract term',
  'detail.months': '{{months}} months',
  'detail.renewable': 'Renewable content',
  'detail.tdsp': 'Delivery utility (TDU)',
  'detail.etf': 'Early termination fee',
  // ── PLAIN LANGUAGE — no ERCOT code, no raw enum (E-SF19 P2) ───────────────
  // The price breakdown used to print the catalogue's own wire tokens
  // (`REP_ENERGY`, `TDU_PASSTHROUGH`) as line-item names. These are the customer's
  // words for the same things. A component this build cannot name is SUPPRESSED,
  // never printed as its code — see `plan-language.service.ts`.
  'detail.component.repEnergy': 'Electricity from your provider',
  'detail.component.tduDelivery': 'Delivery to your home (utility charge)',
  'detail.component.repBase': 'Provider monthly base charge',
  'detail.component.tduFixed': 'Utility monthly meter charge',
  'detail.component.ancillary': 'Grid services (ERCOT)',
  'detail.component.repCosts': 'Provider costs',
  'detail.component.taxes': 'Texas state and local taxes',
  // Rate TYPE, so a badge never shouts a contract enum at the customer.
  'detail.rateType.fixed': 'Fixed rate',
  'detail.rateType.variable': 'Variable rate',
  'detail.rateType.indexed': 'Indexed rate',
  'detail.rateType.unknown': 'Rate type',
  'detail.components.title': 'Rate components',

  // Address input (AC1 deep-link entry — E-SF03 owns resolution)
  'address.deepLink.heading': "You're signing up for:",
  'address.label': 'Service address',
  'address.placeholder': '123 Main St, Austin, TX 78701',
  'address.continue': 'Continue',
  'address.next.note': 'Address lookup and serviceability come next.',

  // ── E-SF03 — address → serviceability → ESI resolution ──────────────────
  'address.back': 'Back to plan',
  // Plan detail → credit CTA (SHOP-6: the meter is already resolved; Continue goes
  // straight to the approval/credit step, not a Service address step).
  'detail.continueToCredit': 'Continue to approval',
  'address.heading': "Let's check service at your address",
  'address.session.loading': 'Preparing…',
  'address.session.error.title': "We couldn't start your session",
  'address.session.error.body': 'Something went wrong reaching the storefront. Please try again.',

  'address.form.line1.label': 'Street address',
  'address.form.line1.required': 'Please enter your street address.',
  'address.form.unit.label': 'Apt / unit (optional)',
  'address.form.city.label': 'City',
  'address.form.city.required': 'Please enter your city.',
  'address.form.zip.label': 'ZIP',
  'address.form.zip.invalid': 'Enter a 5-digit ZIP code.',
  'address.form.check': 'Check my address',
  'address.form.checking': 'Checking your address…',

  // Address autocomplete (demo mock) — typeahead over serviceable TX addresses
  'address.suggest.loading': 'Searching addresses…',
  'address.suggest.empty': 'No matching serviceable addresses — keep typing your full address.',
  'address.suggest.error': "Couldn't load suggestions — keep typing your full address.",
  'address.suggest.listLabel': 'Serviceable address suggestions',
  'address.suggest.cityLine': ' — {{city}}, {{state}} {{zip}}',

  'address.error.title': "We couldn't check that address",
  'address.error.body': 'Something went wrong. Please try again in a moment.',

  'address.tdsp': 'Delivery: {{tdsp}}',
  'address.enroll.switch': 'Switching provider',
  'address.enroll.moveIn': 'Moving in',
  'address.plan.available': 'This plan is available here',
  'address.plan.unavailable': "This plan isn't available at your utility",

  'address.candidate.esiid': 'Your ESI ID: {{esiid}}',
  'address.candidate.select': 'This is my address',
  'address.ambiguous.title': 'We found more than one match',
  'address.ambiguous.body': 'Pick the premise that matches your home.',
  'address.resolved.title': 'We found your premise',
  'address.resolved.next': 'Next: your estimated bill and sign-up. Those steps are coming soon.',

  'address.notFound.title': "We couldn't find that address",
  'address.notFound.body':
    "We serve this area, but couldn't match that exact address. Check it and try again.",

  // ── E-SF20 — new construction (no ESI ID exists yet) ────────────────────
  // Offered UNDER the not-found message, never instead of it: an empty result in
  // a served area is far more often a typo than a new build.
  'newService.prompt':
    "Building a new home? It won't be listed yet — new homes have no meter until the utility installs one.",
  'newService.open': "It's a new build",
  'newService.title': 'Tell us about your new home',
  'newService.body':
    "New homes don't have a meter until the utility installs one. Tell us about the build and we'll give you a link to check back on.",
  // Street fields — collected ONLY on the ZIP on-ramp, where the address cannot
  // be in the suggestion list because the premise has no meter to list it by.
  'newService.line1.label': 'Street address',
  'newService.line1.required': 'Please enter the street address of the new home.',
  'newService.city.label': 'City',
  'newService.city.required': 'Please enter the city.',
  'newService.zip.fixed': 'ZIP {{zip}}',
  'newService.premiseType.label': 'What kind of home is it?',
  'newService.premiseType.required': 'Please choose the kind of home.',
  'newService.premiseType.SINGLE_FAMILY': 'House',
  'newService.premiseType.TOWNHOME': 'Townhome',
  'newService.premiseType.MULTI_FAMILY': 'Apartment or condo building',
  'newService.premiseType.MANUFACTURED': 'Manufactured home',
  'newService.squareFeet.label': 'Approximate size in square feet (optional)',
  'newService.squareFeet.help':
    "If you don't know yet, leave it blank — we'll use a typical new home for your area.",
  'newService.squareFeet.invalid': 'Enter a size between 200 and 25,000 square feet.',
  'newService.unitCount.label': 'How many units (optional)',
  'newService.unitCount.help':
    'Each unit gets its own meter, so we may need to confirm which one is yours later.',
  'newService.unitCount.invalid': 'Enter a number of units between 1 and 500.',
  'newService.occupancy.label': 'When do you expect to move in (optional)',
  'newService.occupancy.help': "We'll use this to know when to expect your meter.",
  // US-14 — the green tag is the commonest cause of delay and the one thing the
  // customer can chase, so it is worth asking for even though it is optional.
  'newService.permit.label': 'Electrical permit number (optional)',
  'newService.permit.help': 'From your builder or your city — it helps us explain any delay.',
  'newService.builder.label': 'Builder (optional)',
  'newService.email.label': 'Email',
  'newService.email.placeholder': 'you@example.com',
  'newService.email.invalid': 'Please enter a valid email address.',
  'newService.submit': 'Save my details',
  'newService.submitting': 'Saving…',
  'newService.error': "We couldn't save your details. Please try again.",
  'newService.thanks.title': "Thanks — we've saved your new home",
  // ⚠️ HONEST COPY. It promises a link to check back on, and NOTHING about being
  // notified: the detection loop (US-02) and the notification (US-13) are not
  // built. Do not add "we'll email you when it's ready" until they are.
  'newService.thanks.body':
    "We've emailed you a link. Use it whenever you want to check whether your meter has been installed.",
  // ⚠️ Shown when the confirmation email did NOT reach a relay (`emailSent:false`).
  // Saying "check your inbox" for a message nobody accepted is the falsehood the
  // server's `emailSent` flag exists to prevent.
  'newService.thanks.bodyNoEmail':
    "We couldn't send you an email just now, so save this link — it's the only copy you'll get.",
  'newService.thanks.ref': 'Your link: {{url}}',

  // ── E-SF20 — the pending-service page at /new-service/:ref ──────────────
  'pendingService.heading': 'Your new home',
  'pendingService.loading': 'Checking your new home…',
  'pendingService.invalid.title': "This link isn't valid",
  'pendingService.invalid.body':
    "This link may have expired, or it may have been mistyped. Search your address again to start over.",
  'pendingService.error.title': "We couldn't load your new home",
  'pendingService.error.body': 'Something went wrong. Please try again in a moment.',
  'pendingService.declaredOn': 'You told us on',
  // Deliberately "your", not "expected connection date" — we never receive a
  // utility timeline, and presenting a guess as a commitment would be a lie.
  'pendingService.yourExpectedDate': 'The date you expected to move in',
  'pendingService.premise': 'Matched to: {{premise}}',
  // ⚠️ "Check back" — NOT "we'll let you know". Nothing detects (US-02) and
  // nothing notifies (US-13).
  'pendingService.checkBack': 'Come back to this link any time to check again.',
  'pendingService.shopCta': 'Shop electricity plans',
  'pendingService.state.waiting.heading': "Your meter isn't installed yet",
  'pendingService.state.waiting.body':
    "New homes get their meter from the utility, usually a few months into the build. There's nothing to sign up to until then.",
  'pendingService.state.progress.heading': 'Your meter is on its way',
  'pendingService.state.progress.body':
    "The utility has registered your home. You'll be able to sign up once it starts recording usage.",
  'pendingService.state.live.heading': 'Your meter is live',
  'pendingService.state.live.body': 'Your home is ready — you can choose a plan and sign up now.',
  'pendingService.state.superseded.heading': 'This address already has service',
  'pendingService.state.superseded.body':
    "It turns out this address already has a meter, so there's nothing to wait for. Search for it to sign up.",
  'pendingService.state.abandoned.heading': "We've closed this",
  'pendingService.state.abandoned.body':
    "We're no longer tracking this address. If you're still building, tell us about it again.",
  'pendingService.overdue.title': 'This is taking longer than you expected',
  'pendingService.permit': 'Electrical permit',
  'pendingService.overdue.body':
    "The date you gave us has passed and your meter still isn't installed. New builds slip for all sorts of reasons — permits, inspections, connection work. Your utility can tell you where things stand.",
  // US-14 — with a permit number the advice becomes actionable rather than a shrug.
  'pendingService.overdue.bodyWithPermit':
    "The date you gave us has passed and your meter still isn't installed. The usual hold-up is the electrical inspection. Your city's inspections office can tell you where permit {{permit}} stands, and your builder can chase the utility connection.",

  // Out-of-area + lead capture (AC2)
  'address.outOfArea.title': "We don't serve your area yet",
  'address.outOfArea.body':
    "It looks like your address is outside the areas we currently serve. You can register your interest below.",
  'address.lead.label': 'Email (optional)',
  'address.lead.placeholder': 'you@example.com',
  'address.lead.submit': 'Register interest',
  'address.lead.submitting': 'Saving…',
  'address.lead.thanks': "Thanks — we've registered your interest in this area.",
  'address.lead.email.invalid': 'Please enter a valid email address.',
  'address.lead.unavailable':
    "Sign-up notifications aren't available yet. Please check back soon.",
  'address.lead.error': "We couldn't save your details. Please try again.",

  // EFL disclosure (AC2)
  'efl.title': 'Electricity Facts Label',
  'efl.official.badge': 'Official EFL',
  'efl.docs.withContract': 'Provided in full with your contract before you sign.',
  'efl.preview.note':
    'This Electricity Facts Label preview is built from catalogue data. Generation-mix figures are development placeholders and the Terms of Service / Your Rights as a Customer are placeholder text. It is not a publishable PUCT §25.475 EFL.',
  'efl.tos.title': 'Terms of Service',
  'efl.yrac.title': 'Your Rights as a Customer',

  // Address → credit handoff (E-SF05)
  'address.resolved.continue': 'Continue to credit check',

  // Identity, credit & deposit decision (E-SF05)
  'credit.heading': 'Verify your identity',
  // ⚠️ `credit.subheading` was REMOVED, not renamed. It restated the FCRA/PUCT
  // consent copy and the SSN-handling note, both of which appear at the field the
  // customer actually acts on. Do not reinstate it as a preamble — if a disclosure is
  // missing, add it where the decision is made.
  'credit.back': 'Back to plan',
  // Shown INSTEAD of the above once a decision is on screen: from there the previous
  // step is the form on this same page, not the plan two steps up the funnel.
  'credit.backToDetails': 'Back to your details',
  'credit.noCandidate.title': 'Start with your address',
  'credit.noCandidate.body':
    'We need your resolved service address before the credit step. Please go back and check your address.',
  // Shown to a RETURNING tab after a refresh. It must be specific about what did NOT
  // come back: two blank fields with no explanation reads as a broken form, and the
  // reason those two are blank (we never store them) is the reassuring part.
  'credit.rehydrated.title': 'We picked up where you left off',
  'credit.rehydrated.body':
    "Your verified email address was restored, so there is no need to request another code. For your security we never return your Social Security Number, driver's license or date of birth to this page, so please enter those again.",
  // Shown to a customer who arrived from an agent's emailed offer, whose details we
  // already held. It must NOT reuse the refresh copy above: "picked up where you left
  // off" and "another code" are both false for someone who has never been here and
  // was never sent one.
  //
  // ⚠️ AND IT MUST NOT SAY *WHY* THE ADDRESS IS CONFIRMED. An offer link sent with no
  // recipient proves nothing at entry, so that customer runs a normal code and then
  // sees this same notice — "confirmed because you clicked our link" would be untrue
  // in exactly that case. Confirmed is true on both routes in.
  'credit.prefilled.title': 'We have filled in what we already had',
  'credit.prefilled.body':
    "Your details came from the offer we sent you, and your email address is already confirmed, so there is no need to request a code. Please check they are correct. For your security we never return your Social Security Number, driver's license or date of birth to this page, so we still need those.",
  'credit.form.firstName.label': 'First name',
  'credit.form.firstName.required': 'Enter your first name.',
  'credit.form.lastName.label': 'Last name',
  'credit.form.lastName.required': 'Enter your last name.',
  'credit.form.dob.label': 'Date of birth',
  // US month-first order. This is a real placeholder on a text input — a native
  // <input type="date"> shows the BROWSER's locale format instead, which cannot be
  // overridden and rendered as dd-mm-yyyy here. See utils/dob-format.ts.
  'credit.form.dob.placeholder': 'mm-dd-yyyy',
  'credit.form.dob.pickerAria': 'Open the calendar to pick your date of birth',
  // @fluxora/ui ships no English — the DatePicker takes every label as a prop, so a
  // shared primitive can never render untranslated text in one of the six apps.
  'credit.form.dob.prevMonth': 'Previous month',
  'credit.form.dob.nextMonth': 'Next month',
  'credit.form.dob.monthSelect': 'Month',
  'credit.form.dob.yearSelect': 'Year',
  'credit.form.dob.invalid': 'Enter your date of birth.',
  // The 18+ rule. Kept as ONE key rather than adding a second with the same words —
  // the calendar greys these days out, and this is what a TYPED or PASTED date gets.
  'credit.form.dob.adult': 'You must be at least 18 years old to enroll.',
  // Its own message since 2026-08-04. A year like 1090 used to be answered with
  // "you must be at least 18 years old", which reads as an accusation rather than a
  // correction — a mistyped year is overwhelmingly likelier than a 936-year-old.
  'credit.form.dob.implausible': 'Check the year — that date of birth is too far in the past.',
  'credit.form.email.label': 'Email address',
  'credit.form.email.required': 'Enter your email address.',
  'credit.form.email.invalid': 'Enter a valid email address.',
  // E-SF05 — the address must be PROVEN before the credit check. The second message
  // is the "verified, then edited the field" case: the copy names what changed, so
  // the customer is not left staring at a disabled button.
  'credit.form.email.unverified': 'Verify this email address to continue.',
  'credit.form.email.changed':
    'You changed your email address. Verify the new one to continue.',
  'credit.form.phone.label': 'Mobile phone',
  'credit.form.phone.note': 'We use this to send enrollment and service updates.',
  'credit.form.phone.required': 'Enter your mobile phone number.',
  'credit.form.phone.invalid': 'Enter a valid 10-digit US phone number.',
  'credit.form.ssn.label': 'Social Security Number',
  'credit.form.ssn.note': 'Used only for the credit check.',
  'credit.form.ssn.show': 'Show Social Security Number',
  'credit.form.ssn.hide': 'Hide Social Security Number',
  'credit.form.ssn.required': 'Enter your Social Security Number.',
  'credit.form.ssn.invalid': 'Enter a valid 9-digit Social Security Number.',
  // Separate from `.invalid` on purpose: "enter a valid SSN" is unhelpful when the
  // customer can see nine-plus digits in the field and the real problem is the count.
  'credit.form.ssn.tooLong': 'A Social Security Number is exactly 9 digits.',

  // ── THE ALTERNATIVE IDENTIFIER ─────────────────────────────────────────────
  // US WORDING, DELIBERATELY, AND NOT THE BRITISH FORM. The document is a
  // "Driver's License" (possessive, `-se`); "driving licence" is not what any US
  // card, agency or applicant calls it, and on an enrollment form that asks for a
  // government ID the customer must recognise the words as naming the thing in
  // their wallet.
  //
  // "or State ID" is not padding either: roughly one adult in ten does not drive,
  // and every state issues a non-driver ID card through the same agency, with a
  // number in the same field. Labelling this "Driver's License" alone tells those
  // applicants the alternative is not for them — which defeats the point of
  // having one.
  'credit.form.identity.or': 'or',
  'credit.form.identity.required':
    "Enter your Social Security Number, or your driver's license or state ID.",
  'credit.form.driversLicense.label': "Driver's License or State ID Number",
  // Says what the bound IS rather than "invalid", because the formats genuinely
  // differ by state and a customer copying an unusual one off their card needs to
  // know which part we object to.
  // Texas gets its own message: a generic "letters and numbers" one is actively
  // WRONG for a TX licence and would send someone hunting for a letter to add. A
  // 7-digit legacy number is padded, not refused, so the count named here is the
  // only one a customer can act on.
  'credit.form.driversLicense.texas': 'A Texas driver’s license number is 8 digits.',

  'credit.form.check': 'Run credit check',
  'credit.form.checking': 'Checking…',
  // ── WHY "Run credit check" is disabled ───────────────────────────────────
  // Names the CONDITION and the control that clears it, never our internal
  // state. A customer who has filled in every field and ticked every box must
  // not be left guessing which of a dozen inputs the page is unhappy with.
  // ⚠️ Must not contain “invalid”, “required field” or “verification state” — the
  // copy names the customer's next physical action, never our model of the form.
  // `CreditPage.test.tsx` asserts that, and it is the whole point of these strings.
  'credit.blocked.title': 'One more step before we can run the check',
  'credit.blocked.emailUnverified':
    'Confirm your email address first: select “Email me a code” above, then type the 6-digit code we send you. We cannot run the credit check until this address is confirmed.',
  'credit.blocked.consents':
    'Tick all three acknowledgements above before we can run the check.',
  'credit.consent.fcra.label': 'I authorize a credit check (FCRA)',
  'credit.consent.fcra.copy':
    'I authorize the retail electric provider to obtain my consumer credit report from a nationwide consumer reporting agency to determine deposit requirements, as permitted by the Fair Credit Reporting Act.',
  'credit.consent.fcra.required': 'FCRA authorization is required to continue.',
  'credit.consent.puct.label': 'I understand a deposit may be required (PUCT §25.478)',
  'credit.consent.puct.copy':
    'I understand that, based on this check, a deposit may be required before service begins, and that PUCT §25.478 waiver options may be available to me.',
  'credit.consent.puct.required': 'Please acknowledge the deposit disclosure.',
  // ⚠️ PROVISIONAL COPY, PENDING COUNSEL (as of 2026-07-28). A SEPARATE tick from the
  // FCRA authorization above — never merged with it, never restating it. The version
  // of this text that the customer saw is stamped on the request
  // (UNDERTAKING_VERSION_PLACEHOLDER) and is deliberately NEVER rendered on screen.
  'credit.consent.undertaking.label':
    'I certify that the information I have provided above is true and correct.',
  'credit.consent.undertaking.required':
    'Please confirm the information you provided is true and correct.',

  // ── E-SF05 email OTP — proof of control of the contact address ─────────────
  // Every notice this enrollment sends (status link, contract copy, FCRA
  // adverse-action notice, welcome pack) goes to this address, so it is proven
  // BEFORE the bureau call rather than after.
  // ⚠️ E-SF19 P3 — shown after a REFRESH when a code is still live. It cannot name
  // the address: the pending recipient is unproven and never leaves the server. It
  // says only what is honestly known — one is already out there — so the customer
  // does not spend one of three hourly sends getting a second.
  'credit.otp.outstanding':
    'We’ve already emailed you a code and it’s still valid — check your inbox (and spam) before asking for another.',
  'credit.otp.send': 'Email me a code',
  'credit.otp.reload': 'Reload to continue',
  // The FIRST-send affordance while the server's 60-second floor is still running —
  // which happens on a refresh, when a live challenge exists but the SPA cannot match
  // it to the address in the field. Worded as "wait" rather than "resend" because from
  // the customer's seat nothing has been sent from THIS screen yet; `credit.otp.resendIn`
  // is the equivalent line once they are visibly holding a code.
  'credit.otp.sendIn': 'You can request a code in {{seconds}}s',
  'credit.otp.sending': 'Sending your code…',
  'credit.otp.sent': 'We emailed a 6-digit code to that address. Enter it below.',
  'credit.otp.code.label': 'Verification code',
  'credit.otp.code.required': 'Enter the 6-digit code we emailed you.',
  'credit.otp.code.invalid': 'That code should be 6 digits.',
  'credit.otp.verify': 'Verify code',
  'credit.otp.verifying': 'Verifying…',
  'credit.otp.resend': 'Send a new code',
  'credit.otp.resendIn': 'You can request a new code in {{seconds}}s',
  'credit.otp.retry': 'Try again',
  // Since 2026-08-04 this string is rendered ONLY as the accessible name (aria-label +
  // title) of the tick inside the email field — the full-width success pill it used to
  // label is gone. It is deliberately still a translated string and not dropped: the
  // visible confirmation became a glyph, and a glyph with no name tells a screen-reader
  // user nothing at all.
  'credit.otp.verified': 'Email verified',
  'credit.otp.verifiedNote':
    'This address is confirmed. If you change it, we’ll need to send a new code.',
  // The server's own answers, kept distinct: "expired" and "too many attempts" need
  // different words, and collapsing them would make one of the two messages a lie.
  'credit.otp.outcome.incorrect':
    "That code doesn't match. {{attempts}} attempt(s) remaining.",
  'credit.otp.outcome.expired': 'That code has expired. Request a new one.',
  'credit.otp.outcome.locked': 'Too many attempts. Request a new code to try again.',
  'credit.otp.error.title': "We couldn't send your code",
  'credit.otp.error.verifyTitle': "We couldn't check your code",
  'credit.otp.error.body': 'Something went wrong. Please try again.',
  // HONEST 501 — SMTP credentials are still pending. Never a fake success.
  'credit.otp.error.notAvailable':
    "Email verification isn't available yet — this step is still being connected. Please check back soon.",
  // 401 — no funnel session (a deep link straight to this step). The truthful fix is
  // the address step, which is the only route that legitimately starts a session.
  'credit.otp.error.sessionExpired':
    'Your session has expired. Please go back and start with your address.',
  'credit.error.title': "We couldn't complete the credit check",
  'credit.error.body': 'Something went wrong. Please try again.',
  // E-SF05 — Accounts refused the identity number before any bureau call.
  //
  // ⚠️ SAYS ONLY THAT THE NUMBER COULD NOT BE READ. It must not distinguish a
  // malformed number from one that failed verification: those are different
  // answers about a real person's identity, and a caller who can tell them apart
  // has an enumeration oracle. The storefront is deliberately told less than the
  // server knows — see `credit-error.service.ts`.
  //
  // "Check the nine digits" is a format reminder the field itself already
  // enforces, so it reveals nothing the form does not.
  'credit.error.identityInvalid.title': 'Check the number you entered',
  'credit.error.identityInvalid.body':
    "We couldn't read the Social Security or ITIN number you entered. Please check the nine " +
    'digits and try again.',
  'credit.error.notAvailable':
    "Credit checks aren't available yet — this step is still being connected. Please check back soon.",
  // The licence-only refusal. It names the ONE field to fill in and says why, so
  // the shopper is not left re-pressing a button that cannot succeed. It also
  // reassures them that the licence they typed was not wasted — it was kept, and
  // they do not have to enter it again.
  'credit.error.identityNumberRequired.title': 'We need your Social Security Number',
  'credit.error.identityNumberRequired.body':
    "A credit check has to be run against a Social Security Number, so a driver's license on its own isn't enough yet. Add your Social Security Number above and run the check again — we've kept the rest of your details.",
  'credit.approve.title': "You're approved — no deposit required",
  'credit.approve.body':
    'Good news: no deposit is needed. The next step is to review and sign your contract.',
  'credit.waived.title': 'No deposit required — a waiver applies',
  'credit.waived.body':
    'Based on your credit decision, a statutory PUCT §25.478 waiver applies, so no deposit is needed. The next step is to review and sign your contract.',
  'credit.continueToEstimate': 'See your estimated bill →',

  // ⚠️ PROVISIONAL-DEPOSIT DISCLOSURE. Shown unless a deployment positively asserts
  // VITE_DEPOSIT_AMOUNT_VERIFIED=true. Same register as the bill-estimate
  // disclaimer below ("an estimate, not a guaranteed price") on purpose — the
  // storefront should make ONE kind of promise about provisional money, not two.
  // Remove the gate (not the copy) once a real per-customer projection reaches the
  // §25.478 calculation. See deposit-disclosure.service.ts.
  'deposit.provisional.badge': 'Estimate — not a final charge',
  'deposit.provisional.title': 'This deposit amount is an estimate, not a final charge',
  'deposit.provisional.body':
    'The figure shown here is a placeholder for this preview environment — it has not yet been worked out from your own estimated annual bill, so it is not an amount you would be charged. Any deposit we require is individually determined under PUCT §25.478, capped at one fifth of your estimated annual billing, and the real figure is shown on the agreement you sign before anything is owed.',

  // Deposit decision + §25.478 waiver matrix (E-SF05 AC1 / AC2)
  'deposit.required.title': 'Nothing to pay today',
  'deposit.required.body':
    'Based on your credit check, a {{amount}} deposit applies to your account (PUCT §25.478) — but you will NOT pay it as a lump sum today. We spread it across your first bills. Just set up your payment method below to continue, or request a waiver if you qualify.',
  // Deferred/installment schedule line, shown when an installment plan is offered.
  'deposit.deferred.schedule':
    'How your {{amount}} deposit is spread: {{first}}% is added to your first bill, and the remainder within {{remainderDays}} days — never a single up-front charge.',
  'deposit.pay.title': 'Set up your payment method',
  'deposit.card.savedNext': "Nothing was charged today — your deposit rides your first bills. Next, see what this plan costs at your usage.",
  'deposit.waiver.clear': 'Clear selection — set up a card instead',
  'deposit.pay.saqa':
    'Your payment details are entered securely and never touch our servers. Nothing is charged today — this just puts a payment method on file for your deposit installments and future bills.',
  'deposit.pay.cta': 'Set up your payment method',
  'deposit.pay.starting': 'Preparing…',
  'deposit.pay.error': "We couldn't set up your payment method. Please try again.",
  'deposit.pay.stubNote':
    'Payment-method setup is being connected — nothing has been saved or charged. If you qualify, you can request a waiver below, or check back soon.',
  // The synthetic gateway saves a payment method on file (no charge); the deposit
  // is billed later in installments. Copy never charges a lump sum at signup.
  'deposit.pay.demo.note':
    'Your payment method is saved on file for your deposit installments and future bills. Nothing is charged today.',
  'deposit.pay.demo.card': 'Card number',
  'deposit.pay.demo.exp': 'MM/YY',
  'deposit.pay.demo.expLabel': 'Expiry',
  'deposit.pay.demo.cvc': 'CVC',
  'deposit.pay.demo.pay': 'Save payment method',
  'deposit.pay.demo.paying': 'Saving…',
  'deposit.pay.demo.paidTitle': 'Payment method saved',
  'deposit.pay.demo.paidBody':
    "You're all set — nothing was charged today. Your {{amount}} deposit will be billed in installments across your first bills.",
  'deposit.pay.elementsSlot': 'The secure card field will appear here.',
  // No-Stripe masked "card on file" (CardOnFileForm). The card number never leaves
  // the browser — only the masked brand/last4/expiry are saved. No CVV is asked for,
  // as in the Customer Portal and the CRM. Per-field errors come from cardEntry.ts,
  // worded exactly as those two apps word them.
  'deposit.card.number': 'Card number',
  'deposit.card.expLabel': 'Expiry',
  'deposit.card.expPlaceholder': 'MM/YY',
  'deposit.card.brand': 'Card brand',
  'deposit.card.error': "We couldn't save your card. Please try again.",
  'deposit.card.saving': 'Saving…',
  'deposit.card.save': 'Save card · {{amount}} deposit',
  // Used while the §25.478 figure is provisional (see deposit.provisional.*): the
  // button must not name a placeholder as the charge being set up.
  'deposit.card.save.provisional': 'Save card · estimated {{amount}} deposit',
  'deposit.card.savedTitle': 'Card saved',
  'deposit.card.savedBody':
    'Your {{brand}} card ending {{last4}} (exp {{exp}}) is on file. Nothing was charged today — your deposit is billed in installments across your first bills.',
  'deposit.waiver.title': 'Deposit waiver options (PUCT §25.478)',
  'deposit.waiver.intro':
    'If one of these applies to you, select it to request a waiver instead of paying a deposit. Waiver requests are subject to verification before service begins.',
  'deposit.waiver.age62.label': 'I am 62 years of age or older',
  'deposit.waiver.age62.desc': 'Customers 62 and older may qualify for a deposit waiver.',
  'deposit.waiver.familyViolence.label': 'I am a certified victim of family violence',
  'deposit.waiver.familyViolence.desc':
    'Requires a certification letter from a qualifying agency or court.',
  'deposit.waiver.medicalIndigence.label': 'Serious illness / medical indigence in my household',
  'deposit.waiver.medicalIndigence.desc': 'Requires a statement from a physician or hospital.',
  'deposit.waiver.lowIncomeInstallment.label': 'Low income — pay the deposit in installments',
  'deposit.waiver.lowIncomeInstallment.desc':
    'Split the deposit into two payments instead of paying it all at once.',
  'deposit.waiver.guarantor.label': 'A guarantor will cover my deposit',
  'deposit.waiver.guarantor.desc': 'A qualified guarantor stands in place of the deposit.',
  'deposit.waiver.stub.badge': 'Not available yet',
  'deposit.waiver.stub.unavailable':
    "The guarantor option isn't available yet. Please choose another option or pay the deposit.",
  'deposit.waiver.certRef.label': 'Certification reference (optional)',
  'deposit.waiver.certRef.placeholder': 'e.g. case or program number',
  'deposit.waiver.certRef.note':
    "You can provide your documentation reference now; we'll verify it before service begins.",
  'deposit.waiver.installment.note':
    'You would pay {{first}}% within {{firstDays}} days and the remainder by day {{remainderDays}}.',
  'deposit.waiver.submit': 'Request waiver',
  'deposit.waiver.submitting': 'Submitting…',
  'deposit.waiver.error': "We couldn't record your request. Please try again.",
  'deposit.waiver.recorded.title': 'Request recorded',
  'deposit.waiver.recorded.waived':
    'Your waiver request has been recorded and will be verified before service begins.',
  'deposit.waiver.recorded.installment':
    'Your installment plan has been recorded — you will pay the deposit in two parts.',

  // ── E-SF06 — consent, click-wrap sign & instant-accept ──────────────────
  // Names its destination, like every other step's back link. A bare "Back" on the
  // last screen before signing gives no clue whether it abandons the enrollment.
  'sign.back': 'Back to estimate',
  'sign.heading': 'Review and sign',
  'sign.subheading':
    'Choose your start date, review your documents, and sign to complete your enrollment.',
  'sign.loading': 'Preparing your agreement…',
  'sign.noCandidate.title': 'Start with your address',
  'sign.noCandidate.body':
    'We need your resolved service address before you can sign. Please go back and check your address.',
  'sign.error.title': "We couldn't load your agreement",
  'sign.error.body': 'Something went wrong. Please try again in a moment.',
  'sign.error.notAvailable':
    "Signing isn't available yet — this step is still being connected. Please check back soon.",

  // Start-date picker
  'sign.startDate.heading': 'When should your service start?',
  'sign.startDate.switch.note':
    "You're switching providers. Pick the first available date, or choose a date that works for you.",
  'sign.startDate.moveIn.note': "You're moving in. Choose your service start date.",
  'sign.startDate.firstAvailable.label': 'First available date',
  'sign.startDate.firstAvailable.desc': 'The earliest we can start service ({{date}}).',
  'sign.startDate.chosen.label': 'Choose a date',
  'sign.startDate.chosen.desc': 'Pick a start date that works for you.',
  'sign.startDate.date.label': 'Service start date',
  'sign.startDate.offered.note':
    'Your energy advisor set your start date to {{date}}. Keep it, or change it below.',
  'sign.startDate.sameDay.label': 'Request same-day start',
  'sign.startDate.sameDay.desc':
    'Your meter supports a same-day start if requested before {{cutoff}}. Same-day start is subject to your utility.',
  'sign.startDate.error.required': 'Please choose a valid service start date.',
  'sign.startDate.error.range': 'Please choose a date within the available window.',

  // Rescission disclosure (switches, AC3)
  'sign.rescission.title': 'Your right to cancel (switch)',
  'sign.rescission.body':
    'Because you are switching providers, you have the right to cancel this agreement within three federal business days after you sign, without any penalty or early-termination fee. Your exact cancellation deadline is shown on your confirmation.',

  // ── E-SF18 — the contract, rendered where it is signed ───────────────────
  // The customer reads their REAL unsigned agreement here. Copy rules that are
  // load-bearing rather than stylistic:
  //   - never call it "your contract is ready" while it is still UNSIGNED;
  //   - never describe a slow render as an error — nothing is broken and nothing
  //     is lost, so "not ready yet" is the truth and "failed" is not;
  //   - the wait is NAMED at each escalation, because an unchanging message over a
  //     multi-second render is indistinguishable from a hung page.
  'sign.document.heading': 'Your contract',
  'sign.document.intro':
    'This is the agreement you are about to sign. Please read it, then confirm below.',
  'sign.document.idle': 'Your contract will appear here once your plan and address are confirmed.',
  'sign.document.loading.start': 'Preparing your contract…',
  'sign.document.loading.slow': 'Still preparing your contract — this usually takes a few seconds.',
  'sign.document.loading.verySlow':
    'Your contract is taking longer than usual. Hold on a moment — if it does not appear, you will be able to try again.',
  'sign.document.retry': 'Try again',
  'sign.document.viewer.label': 'Your contract, as a PDF document',
  'sign.document.openInNewTab': 'Open your contract in a new tab',
  'sign.document.fallback.body':
    'Your browser cannot show the contract on this page. Open it in a new tab to read it, or use the text version below.',
  'sign.document.noViewer.title': 'We cannot display your contract here',
  'sign.document.noViewer.body':
    'Your contract was prepared but this browser cannot display it on the page. Please use the text version below, which contains the same terms.',
  'sign.document.textVersion.summary': 'Read the text version instead',
  'sign.document.textVersion.note':
    'The same terms in plain text, for screen readers and for anyone who prefers not to use the PDF viewer.',
  // NOT an error title: the enrollment is fine, the document just was not drawn
  // this time. Trying again genuinely works.
  'sign.document.error.retryable.title': 'Your contract is not ready yet',
  'sign.document.error.retryable.body':
    'We could not prepare your contract just now. Nothing has been signed and nothing was lost — please try again.',
  'sign.document.error.notReady.title': 'We need a few details first',
  'sign.document.error.notReady.body':
    'Your contract cannot be prepared yet. Please go back and confirm your plan and start date, then return to this step.',
  'sign.document.error.sessionLost.title': 'Your session has expired',
  'sign.document.error.sessionLost.body':
    'For your security we could not keep your details. Please start again from your address.',
  'sign.document.error.unknown.title': "We couldn't show your contract",
  'sign.document.error.unknown.body':
    'Something went wrong while preparing your contract. Please try again.',

  // Click-wrap consent
  'sign.consent.heading': 'Your documents',
  // Sits directly above the three acknowledgements, immediately below the PDF, so
  // the acceptance reads as a response to the document rather than to a checkbox.
  'sign.consent.afterReview': 'Having read the contract above, please confirm:',
  'sign.consent.efl.label': 'I have read and accept the Electricity Facts Label (EFL).',
  'sign.consent.tos.label': 'I have read and accept the Terms of Service (TOS).',
  'sign.consent.yrac.label': 'I have read and accept Your Rights as a Customer (YRAC).',
  'sign.consent.error.efl': 'Please accept the Electricity Facts Label to continue.',
  'sign.consent.error.tos': 'Please accept the Terms of Service to continue.',
  'sign.consent.error.yrac': 'Please accept Your Rights as a Customer to continue.',
  'sign.consent.tcpa.label': 'Send me account and service text messages (optional)',
  'sign.consent.tcpa.copy':
    'I agree to receive service and account text messages at my mobile number. Message and data rates may apply. Consent is not a condition of enrollment, and I can opt out at any time by replying STOP.',

  // Drawn signature (E-SF17, made COMPULSORY — see `SignGateInput.signatureDrawn`).
  //
  // ⚠️ THIS COPY USED TO SAY "optional", IN FOUR PLACES, AND SAYING SO WAS THE
  // POINT OF IT. It has to stop, because a screen that tells a customer the drawing
  // is optional beside a button that refuses to enable without one is worse than
  // either policy on its own — it reads as a broken page rather than a requirement.
  //
  // What the copy must STILL carry is the ESIGN posture: the drawing is required as
  // a matter of OUR policy, not because it is what binds the customer. The typed
  // name and the checked boxes remain the operative mark, so the strings below ask
  // for the drawing plainly without claiming it is what makes the contract stand up.
  'sign.signature.heading': 'Sign your name',
  // The in-canvas prompt. It is decoration (aria-hidden — the canvas is announced
  // by its heading and hint), and it exists because an empty bordered rectangle at
  // the end of a long page gives a customer no reason to try dragging across it.
  'sign.signature.placeholder': 'Draw your signature here',
  'sign.signature.clear': 'Clear',
  'sign.signature.hint':
    'Draw your signature with a mouse, trackpad or finger. A drawn signature is required to complete your enrollment. Your name and the boxes you checked above are what legally sign this agreement; the drawing is recorded alongside them.',
  'sign.signature.status.drawn': 'A signature has been drawn.',
  // ⚠️ Announced to screen readers, and it must be honest with them above all: this
  // surface cannot be operated without a pointer, and it is now REQUIRED. Telling an
  // assistive-technology user "no signature drawn" and nothing else would leave them
  // hunting for a control that does not exist. See the accessibility note on
  // `SignGateInput.signatureDrawn` — this string is where that cost is spoken aloud.
  'sign.signature.status.empty':
    'No signature drawn. A drawn signature is required, and this pad needs a mouse, trackpad or touchscreen. If you cannot use one, contact us and we will complete your enrollment with you.',
  'sign.signature.error.empty': "That signature didn't come through. Please try drawing it again.",
  'sign.signature.error.notPng':
    "That signature couldn't be read. Please clear it and draw it again.",
  // ⚠️ The "or clear it and sign without drawing" escape that used to end this
  // sentence is GONE — there is no such path any more, and offering one that the
  // gate would refuse is how a customer ends up clicking a disabled button.
  'sign.signature.error.tooLarge':
    'That signature is too large to send. Please clear it and draw a simpler one.',
  // The compulsory-signature failure, as the SUBMIT path reports it. Distinct from
  // `sign.submit.signatureBlocked`, which is the standing reason next to a disabled
  // button: this one answers a customer who pressed sign anyway, so it is phrased as
  // what went wrong rather than as what is pending.
  'sign.signature.error.required':
    'Please draw your signature above before signing — we cannot complete your enrollment without it.',

  // Deposit-settlement gate (deposit collected before accept)
  // ⚠️ THE UNDECIDED STATE — no §25.478 determination exists yet (E-SF19 P1).
  // It must claim NOTHING about whether a deposit is owed. The copy it sits beside
  // ("No deposit required — you have no deposit to pay") was being rendered to a
  // shopper who had reached /sign by URL before any credit check had run.
  'sign.deposit.undecided.title': 'We haven’t worked out your deposit yet',
  'sign.deposit.undecided.body':
    'Whether a deposit is needed is decided at the approval step, from your own estimated annual bill and under PUCT §25.478. Until that runs we can’t tell you either way — finish the approval step and we’ll show you the answer before you sign anything.',
  'sign.deposit.none.title': 'No deposit required',
  'sign.deposit.none.body': 'You have no deposit to pay — you can sign your agreement below.',
  'sign.deposit.settled.title': 'Deposit settled',
  'sign.deposit.settled.paid': 'Your deposit has been collected. You can sign your agreement below.',
  'sign.deposit.settled.waiver':
    'Your deposit waiver is on file and is subject to verification before service begins. You can sign your agreement below.',
  'sign.deposit.required.title': 'A deposit is required before you can sign',
  'sign.deposit.required.body':
    'Please collect your deposit or record a waiver below. You can sign once your deposit is settled.',
  'sign.deposit.unavailable':
    'Deposit details are unavailable right now. Please go back to the deposit step and try again.',

  // ── The signing panel — the end of the step ──────────────────────────────
  // Its own heading and lead-in, because this block has to read as an ARRIVAL. It
  // was previously a bare canvas and a button, styled like the six disclosure
  // blocks above them, and the customer could not find it.
  'sign.panel.heading': 'Sign your agreement',
  'sign.panel.intro':
    'Draw your signature below, then sign and enroll. Your name and the boxes you checked are what legally sign this agreement, and your drawn signature is recorded with them.',

  // Sign control + confirmation
  'sign.submit.cta': 'Sign and enroll',
  'sign.submit.signing': 'Signing…',
  // ── Honest progress for a 14-to-25-second accept ──────────────────────────
  // Each beat names REAL work: the BFF's accept makes three upstream hops (create
  // the account + run the checks, link it to the quote, execute and record the
  // contract). Copy rules that are load-bearing rather than stylistic:
  //   - never claim a stage that has not started;
  //   - never promise a finish line we cannot see ("almost done" on a 25-second
  //     upstream is how a customer decides the page hung and clicks again);
  //   - never say the contract is signed until it is.
  'sign.submit.progress.account': 'Setting up your account…',
  'sign.submit.progress.linking': 'Matching your account to your plan…',
  'sign.submit.progress.executing': 'Recording your signed contract…',
  'sign.submit.progress.doNotClose':
    'This can take up to a minute. Please keep this page open — you only need to sign once.',
  'sign.submit.depositBlocked': 'You can sign once your deposit is settled above.',
  // US-SF18.3 — the reason the primary action is unavailable, STATED, not implied
  // by a greyed-out button. It names what is happening and what will change.
  'sign.submit.documentBlocked':
    'You can sign once your contract has been prepared above — we will not ask you to sign a document you have not seen.',
  // The compulsory-signature gate. It names the ACT ("draw your signature") and the
  // PLACE ("in the box above"), because this reason — unlike the deposit and the
  // contract render — is fixed by doing something a few pixels away, and a customer
  // who has scrolled past the pad needs to be sent back to it, not merely told no.
  'sign.submit.signatureBlocked':
    'Please draw your signature in the box above — a signature is required to complete your enrollment.',
  'sign.submit.consentHint': 'Please accept your documents above to enable signing.',
  'sign.submit.error.title': "We couldn't complete your signing",
  'sign.submit.error.body': 'Something went wrong. Please review your details and try again.',
  // ⚠️ NOT "You're enrolled" any more, and the distinction is not pedantry.
  // E-SF06 made the accept asynchronous: at this moment the SIGNATURE is durable
  // and the enrollment is queued, but no contract exists and no account has been
  // created. Claiming enrollment before the worker has done any of that is a
  // promise we cannot keep if it then fails — and on failure the customer gets a
  // mail saying they were NOT enrolled, which would contradict this screen.
  'sign.confirm.title': 'We have submitted your request.',
  'sign.confirm.body':
    'Your signature is recorded. Service is scheduled to start on {{date}}, and we will email your signed contract shortly.',
  // The RE-DERIVED confirmation — this tab did not do the signing (a refresh, or a
  // second tab), so we know the enrollment is complete but hold none of its dates.
  // We send the customer to the record that has them rather than inventing any.
  'sign.confirm.bodyNoDate':
    'Your agreement is signed. Your start date and cancellation deadline are on your enrollment status page and in your confirmation email.',
  'sign.confirm.rescission':
    'You can cancel without penalty until {{date}} (three federal business days).',
  'sign.confirm.nextSteps.heading': 'What happens next',
  'sign.confirm.statusLink': 'Track your enrollment status',
  // FUTURE TENSE, deliberately. The mail goes out when the worker finishes, which
  // is seconds to minutes after this renders — "we have emailed" would be false at
  // the moment the customer reads it, and the first thing they would do is go
  // looking for a message that has not been sent.
  'sign.confirm.emailNote':
    'We will email your signed contract and a link to this page as soon as your enrollment is complete. You can come back to it at any time.',
  // ⚠️ ITS OWN TITLE since E-SF06. The unlinked panel used to share
  // `sign.confirm.title`, which now reads "We have submitted your request." — accurate at
  // the moment of signing, and wrong here: this path is reached by an OLDER
  // session whose enrollment completed long ago, and pairing it with "already
  // signed, nothing left to do" would read as though we had lost track of it.
  'sign.confirm.unlinkedTitle': 'Your agreement is signed',
  // Signed, but this session holds no status token — an older session cached before
  // the token crossed this boundary. It must still never re-offer signing.
  'sign.confirm.unlinked':
    'Your agreement is already signed, so there is nothing left to do here. Your signed contract and enrollment status were emailed to you — please use the link in that email, or contact support if you no longer have it.',

  // Deep-link source attribution
  'deepLink.badge': 'via {{source}}',
  'deepLink.source.ptc': 'Power to Choose',
  'deepLink.source.organic': 'direct',

  // Personalization — "your estimated bill" (E-SF04)
  'estimate.heading': 'Your estimated bill',
  'estimate.subheading':
    'Here’s what this plan is likely to cost at your home’s usage. It’s an estimate to help you compare — not a bill.',
  'estimate.back': 'Back to credit',
  'estimate.loading': 'Estimating your bill…',
  'estimate.error.title': "We couldn't build your estimate",
  'estimate.error.body':
    'Something went wrong pricing this plan for your premise. Please try again in a moment.',
  'estimate.noCandidate.title': 'Find your address first',
  'estimate.noCandidate.body':
    'We need your service address before we can estimate a bill. Please start from your plan.',
  'estimate.headline.label': 'Estimated monthly cost',
  'estimate.headline.perMonth': '/mo',
  'estimate.headline.effectiveRate': 'About {{rate}}¢ per kWh all-in at your usage',
  // ── USAGE BASIS — SAY WHAT ACTUALLY HAPPENED (E-SF19 P0a) ────────────────
  // One sentence per `EstimateUsageBasis.source`, and each one describes the basis
  // the SERVER reports it priced at. The profile lines are the ONLY ones that may
  // mention missing history, because they are the only case where that is true.
  //
  // ⚠️ The three profile strings used to be the answer for EVERY estimate that did
  // not come from GMO history — including one where the shopper had just typed
  // their own figure into the consumption step. A customer declaring 120 kWh/mo was
  // shown a 500 kWh bill above the words "we couldn't find usage history for this
  // address". Both halves were false: we had their usage, and we discarded it.
  // Never reintroduce a shared fallback sentence here.
  'estimate.basis.declared': 'Based on the {{kwh}} kWh a month you told us you use',
  'estimate.basis.history': 'Based on about {{kwh}} kWh/mo from this home’s recent usage',
  'estimate.basis.profile.small':
    'Based on a typical small home (about {{kwh}} kWh/mo) — you haven’t given us a usage figure and we couldn’t find usage history for this address',
  'estimate.basis.profile.medium':
    'Based on a typical home (about {{kwh}} kWh/mo) — you haven’t given us a usage figure and we couldn’t find usage history for this address',
  'estimate.basis.profile.large':
    'Based on a typical large home (about {{kwh}} kWh/mo) — you haven’t given us a usage figure and we couldn’t find usage history for this address',
  // The neutral key: an estimate whose basis this build does not recognise. It
  // states the figure and claims NOTHING about where it came from, which is the
  // only honest thing to say about a source we cannot describe.
  'estimate.basis.unknown': 'Based on {{kwh}} kWh a month',
  // §25.475 publishes an EFL at 500/1000/2000 kWh only. Outside that band the bill
  // is projected by extending the plan’s published curve — a different claim from
  // reading a published price, so the screen makes it out loud.
  'estimate.basis.belowReferenceRange':
    'Your usage is below the lowest level plans publish a price for (500 kWh/mo), so we’ve extended this plan’s published price curve down to your figure. It’s an estimate, not a published rate.',
  'estimate.basis.aboveReferenceRange':
    'Your usage is above the highest level plans publish a price for (2,000 kWh/mo), so we’ve extended this plan’s published price curve up to your figure. It’s an estimate, not a published rate.',
  // Effective-rate curve
  'estimate.curve.label': 'How the rate changes with usage',
  'estimate.curve.atUsage': 'At {{kwh}} kWh/mo',
  'estimate.curve.unit': '¢/kWh',
  // AC2 disclaimer — never a guaranteed price
  'estimate.disclaimer.title': 'This is an estimate, not a guaranteed price',
  'estimate.disclaimer.body':
    'Your actual bill depends on how much electricity you use, the season, and pass-through delivery charges. It is not a quote or a guaranteed price. Your final rate and terms are shown on the agreement you sign.',
  // Continue → create the DRAFT quote → sign
  'estimate.continue.label': 'Continue to sign',
  'estimate.continue.creating': 'Preparing your agreement…',
  // The GENERIC fallback — kept, and kept generic. It is the honest answer for a
  // failure we do not recognise (an outage, a dropped connection), and the three
  // specific answers below exist precisely so it stops being the ONLY answer.
  'estimate.continue.error.title': "We couldn't prepare your agreement",
  'estimate.continue.error.body':
    'Something went wrong setting up your quote. Please try again in a moment.',
  // ── The three quote-create refusals (QA-2026-08-06) ──────────────────────
  // Each names what happened and what to do next. None of them says "try again":
  // retrying resolves none of these, which is exactly why the shared generic copy
  // above was a dead end when it was the only thing a shopper ever saw.
  //
  // ⚠️ NO IDENTIFIERS IN ANY OF THIS COPY. No ESI ID, no quote id, no ERCOT
  // status, no per-utility ESI ID length. The BFF withholds all of it on purpose
  // (a resumable enrollment link keyed off a meter would be an ESI ID oracle), and
  // the storefront must not reconstruct on screen what the server declined to send.
  'estimate.continue.error.alreadyEnrolled.title': 'This meter is already being switched',
  'estimate.continue.error.alreadyEnrolled.body':
    'There is already an enrollment in progress for this meter, so we cannot start another one. If that was you, check the tracking link in your enrollment email. If it was not, contact our support team and we will sort it out with you.',
  'estimate.continue.error.meterNotActive.title': 'This meter cannot be switched right now',
  'estimate.continue.error.meterNotActive.body':
    'The local delivery utility is not accepting a switch for this meter at the moment. That usually means service is still being set up, has been disconnected, or is on hold. You can try a different meter, or contact our support team.',
  'estimate.continue.error.meterNotActive.action': 'Try a different meter',
  'estimate.continue.error.esiidMismatch.title': "That ESI ID doesn't match this address",
  'estimate.continue.error.esiidMismatch.body':
    'The ESI ID you entered does not belong to the delivery utility that serves this address. Check the ESI ID on your electricity bill and enter it again. If it looks right, contact our support team and we will check the address for you.',
  'estimate.continue.error.esiidMismatch.action': 'Re-enter your ESI ID',
  // ── The three OFFER-path refusals (E-SF16) ──────────────────────────────
  // Reached only by a customer who arrived from an agent's emailed offer, where
  // this step REPOINTS the agent's quote rather than creating one.
  //
  // ⚠️ SAME NO-IDENTIFIERS RULE, and one more: NO PRICES. `planNotInOffer` does
  // not list the offered plans (they are already on the customer's shelf), and
  // `priceChanged` does not name the OLD price — ADR-0010 D1.2 is explicit that the
  // offered figure is evidence of what was offered, never what binds, and the
  // storefront has only ever shown the live one. Naming the old price would
  // introduce a number for the first time in order to say it is out of date.
  'estimate.continue.error.planNotInOffer.title': 'That plan is not part of your offer',
  'estimate.continue.error.planNotInOffer.body':
    'The offer we sent you covers a specific set of plans, and this one is not among them. Choose one of the plans shown on your offer and continue.',
  'estimate.continue.error.planNotInOffer.action': 'Back to your plans',
  'estimate.continue.error.offerUnavailable.title': 'This offer is no longer available',
  'estimate.continue.error.offerUnavailable.body':
    'Your offer has either expired or has already been used. Contact our support team and we will send you an up-to-date one.',
  'estimate.continue.error.priceChanged.title': 'The price for this plan has changed',
  'estimate.continue.error.priceChanged.body':
    'The price for this plan has changed since your offer was sent. The price shown on this page is the current one — continue to enroll at it, or choose a different plan.',
  'estimate.continue.error.priceChanged.action': 'Continue at the current price',
  /** Shown on every specific refusal — the one route to a human. */
  'estimate.continue.error.contactSupport': 'Contact support',

  // ── E-SF16 — the emailed offer entry (`/offer/:token`) ───────────────────
  // A hand-off, not a page the customer stays on: the copy says what is happening
  // and nothing about the offer itself, because at this point the SPA has not seen
  // it (the token is exchanged server-side and the premise never reaches the browser).
  'offer.entry.heading': 'Opening your offer',
  'offer.entry.body': 'Just a moment while we get your plans ready.',
  'offer.entry.loading': 'Opening your offer…',
  // ⚠️ ONE FAILURE PAGE, NO REASON. Unknown, expired, revoked, wrong-purpose and
  // malformed all land here and read identically — the difference between them is
  // information about someone's account, and whoever is holding a link that has
  // been sitting in an inbox is not necessarily the customer. Do not add variants.
  'offer.unavailable.heading': 'This offer link',
  'offer.unavailable.title': 'This link is no longer valid',
  'offer.unavailable.body':
    'We could not open this offer. Offer links are personal and expire after a while, so this one may have lapsed or been replaced. You can browse our plans, or contact our support team and we will send you a fresh offer.',
  'offer.unavailable.shopAction': 'Browse our plans',
  'offer.unavailable.supportAction': 'Contact support',

  // ── E-CRM04 — "Resume your enrollment" ──────────────────────────────────────
  // ⚠️ ONE FAILURE PAGE, NO REASON — the offer page's rule, for the same reason.
  // Unknown, expired, used, revoked and malformed links all read identically.
  'resume.expired.heading': 'Resume your enrollment',
  'resume.expired.title': 'This link has expired or has already been used',
  'resume.expired.body':
    'Resume links are personal, work once and expire after a few days. You can start again — it only takes a few minutes.',
  'resume.expired.startAgain': 'Start again',
  // Too many links opened from this network in the last hour — the link itself
  // was not looked at and still works. Never says "expired".
  'resume.busy.title': 'Too many attempts — please try again shortly',
  'resume.busy.body':
    'We could not open your link just now because of a high number of attempts from your network. Your link has not been used — please click it again in a few minutes.',
  // The welcome-back banner on a session rebuilt from a resume link.
  'resume.banner.title': 'Welcome back — we saved your progress',
  'resume.banner.body':
    'We picked up where you left off. For your security, please re-enter your date of birth and identity details.',
  'resume.banner.dismiss': 'Dismiss',
  // E-CRM04 — every resume link lands here first; Continue redeems it (a mail
  // scanner's prefetch never presses a button, so it cannot spend the link).
  'resume.continue.heading': 'Welcome back',
  'resume.continue.body':
    'Your saved enrollment is ready. Press Continue to pick up where you left off.',
  'resume.continue.continue': 'Continue',
  'resume.continue.startOver': 'Start over instead',
  'resume.continue.loading': 'Getting your enrollment ready…',
  'resume.continue.failedTitle': "We couldn't open your enrollment just now",
  'resume.continue.failedBody': 'Please press Continue again in a moment.',
  // E-CRM04 — the phone check for a resume link sent to an UNVERIFIED email.
  'resume.confirm.heading': 'Welcome back',
  'resume.confirm.body': 'To protect your details, enter the phone number you gave us.',
  'resume.confirm.phoneLabel': 'Phone number',
  'resume.confirm.continue': 'Continue',
  'resume.confirm.startOver': 'Start over instead',
  'resume.confirm.invalid': 'Enter the 10-digit phone number you gave us.',
  'resume.confirm.mismatchOne': "That number doesn't match — 1 try left",
  'resume.confirm.mismatchOther': "That number doesn't match — {{count}} tries left",
  'resume.confirm.failedTitle': "We couldn't check that just now",
  'resume.confirm.failedBody': 'Please try again in a moment.',
  // Neutral: shown on the start page after a lockout, expiry or "start over".
  // Says nothing about why — the person may not be the lead.
  'resume.restart.title': "Let's start again",
  'resume.restart.body': 'We could not restore your saved details, so you can start a new signup below.',
  // E-CRM04 — a link sent to an email that was never verified restores only the
  // energy usage (the plan is not re-shown). No promise that anything is
  // pre-filled: nothing personal is.
  'resume.banner.progress.title': 'Welcome back — we saved your energy usage',
  'resume.banner.progress.body':
    'Enter your service address to continue. For your security we will ask for your contact details again and send a code to confirm your email.',

  // ── E-SF07 — anonymous enrollment status page ────────────────────────────
  'status.heading': 'Your enrollment status',
  'status.loading': 'Checking your enrollment…',
  'status.error.title': "We couldn't load your status",
  'status.error.body': 'Something went wrong. Please try again in a moment.',
  'status.invalid.title': 'This link is not valid',
  'status.invalid.body':
    'This status link is invalid or has expired. Please use the most recent link we emailed you, or contact support.',
  'status.rescission': 'You can still cancel this switch without a fee until {{date}}.',

  // Lifecycle states
  'status.state.PENDING.badge': 'Pending',
  'status.state.PENDING.title': "We've received your enrollment",
  'status.state.PENDING.body':
    "Your enrollment is being set up. We'll email you as soon as a service start date is scheduled.",
  'status.state.SCHEDULED.badge': 'Scheduled',
  'status.state.SCHEDULED.title': 'Your service is scheduled',
  'status.state.SCHEDULED.switchBody': 'Your switch is scheduled to start on {{date}}.',
  'status.state.SCHEDULED.moveInBody': 'Your service is scheduled to start on {{date}}.',
  'status.state.ACTIVE.badge': 'Active',
  'status.state.ACTIVE.title': 'Your service is active',
  'status.state.ACTIVE.body': 'Your electricity service started on {{date}}. Welcome aboard.',
  'status.state.REJECTED.badge': 'Action needed',
  'status.state.REJECTED.title': 'There is a problem with your enrollment',

  // Rejection reasons + next steps
  'status.rejected.SWITCH_HOLD.title': 'Your switch is on hold',
  'status.rejected.switchHold.nextSteps':
    'The current provider placed a hold on this switch. If you are the new occupant of this address, you can clear it below by completing a New Occupant Statement and uploading proof of occupancy.',
  'status.rejected.RESCINDED.title': 'Your enrollment was cancelled',
  'status.rejected.rescinded.nextSteps':
    'This enrollment was cancelled within the cancellation window. If this was a mistake, please start a new enrollment or contact support.',
  'status.rejected.CANCELLED.title': 'Your enrollment was cancelled',
  'status.rejected.cancelled.nextSteps':
    'This enrollment was cancelled. Please contact support if you would like to enroll again.',
  'status.rejected.OTHER.title': 'There is a problem with your enrollment',
  'status.rejected.other.nextSteps':
    'We were unable to complete this enrollment. Please contact support and we will help you sort it out.',

  // Switch-hold self-serve resolution (AC1)
  'status.resolve.title': 'Clear your switch hold',
  'status.resolve.intro':
    'Confirm you are the new occupant of this address and upload proof of occupancy. We will open a case with our team to clear the hold.',
  'status.resolve.priorOccupant.label': 'Prior occupant name (if known)',
  'status.resolve.priorOccupant.note': 'Optional — this helps our team match the hold.',
  'status.resolve.moveInDate.label': 'Your move-in date',
  'status.resolve.moveInDate.invalid': 'Enter a valid move-in date.',
  'status.resolve.occupancyType.label': 'How do you occupy this address?',
  'status.resolve.occupancyType.own': 'I own it',
  'status.resolve.occupancyType.rent': 'I rent it',
  'status.resolve.occupancyType.other': 'Other',
  'status.resolve.occupancyType.required': 'Please tell us how you occupy this address.',
  'status.resolve.proof.label': 'Proof of occupancy',
  'status.resolve.proof.note': 'A lease, deed, or utility bill (PDF, JPG, or PNG, up to ~28 KB).',
  'status.resolve.proof.selected': 'Selected: {{name}}',
  'status.resolve.proof.required': 'Please upload a proof-of-occupancy document.',
  'status.resolve.proof.type': 'That file type is not accepted. Upload a PDF, JPG, or PNG.',
  'status.resolve.proof.size': 'That file is too large. Please upload a smaller document.',
  'status.resolve.proof.read': "We couldn't read that file. Please try another.",
  'status.resolve.acknowledged.label': 'I confirm I am the new occupant of this address',
  'status.resolve.acknowledged.copy':
    'I certify that the information in this statement is true and that I am the new occupant of the service address.',
  'status.resolve.acknowledged.required': 'Please confirm the statement to continue.',
  'status.resolve.submit': 'Submit statement',
  'status.resolve.submitting': 'Submitting…',
  'status.resolve.success.title': 'Your statement has been received',
  'status.resolve.success.body':
    "We've opened a case to clear your switch hold. Your reference is {{ref}}. We'll email you with an update.",
  'status.resolve.error.title': "We couldn't submit your statement",
  'status.resolve.error.notAvailable':
    "Switch-hold resolution isn't available online yet. Please contact support and we'll clear the hold for you.",
  'status.resolve.error.body': 'Something went wrong. Please try again in a moment.',

  // ── Landing page (E-SF08) ─────────────────────────────────────────────────
  // ⚠️ UNDERSTATED, DECLARATIVE REGISTER. This is a regulated retail-energy
  // enrolment, so the copy states what the product does and what the numbers mean —
  // it does not sell. Specifically avoided:
  //   • self-praise      — "priced the honest way", "Compare honest prices"
  //   • competitor jabs  — "not a teaser rate", "No teaser rates", "real plans"
  //   • unownable claims — "true average price" (the PUCT figure is the claim)
  //   • speed promises   — "in minutes" (credit calls a bureau; sign runs a saga
  //                        with a 45s ceiling, so the duration is not ours to promise)
  // Also still true: no fabricated ratings, no invented PUCT numbers, no
  // guaranteed-savings language, and a listing is not an endorsement.
  //
  // The test to apply to new copy: is it a claim about US, or a verifiable statement
  // about what is on screen? Prefer the second.
  'landing.hero.eyebrow': 'Texas · PUCT-regulated electricity',
  'landing.hero.title': 'Compare Texas electricity plans',
  // The headline is split so the gradient can live in the letterforms of the
  // second phrase only — gradient across the WHOLE line reads as decoration;
  // on the two words that matter it reads as emphasis.
  'landing.hero.titleLead': 'Power your home at',
  'landing.hero.titleBrand': 'the right price',
  'landing.hero.subtitle':
    'Every plan is shown at its PUCT average price for your usage, so you can compare like for like — then enrol online.',
  'landing.hero.cta': 'Shop plans',
  // Shown only once a meter is resolved. Names the destination (the method chooser,
  // which is the funnel's first page) and warns that the meter is discarded — this
  // replaced the card's "Start over" button on this page.
  'landing.backToStart': 'Back to start — choose a different meter',

  // ── Step one: who is this? ────────────────────────────────────────────
  // Deliberately short. Four fields before a price is shown is already a lot
  // to ask, so the copy says what it is for and gets out of the way.
  'landing.contact.heading': 'First, who are we quoting for?',
  'landing.contact.firstName': 'First name',
  'landing.contact.lastName': 'Last name',
  'landing.contact.email': 'Email',
  'landing.contact.phone': 'Phone',
  'landing.contact.cta': 'Continue',
  'landing.contact.notice':
    "We'll use this to save your quote and to help you finish if you get stuck. No credit check at this step.",
  'landing.contact.saveFailedTitle': "We couldn't save your details",
  'landing.contact.saveFailedBody':
    'You can carry on shopping — we may ask for them again later.',
  'landing.hero.valueNote':
    'Each headline figure is the PUCT average price at your usage — the figure that reflects your bill.',

  'landing.how.heading': 'How it works',
  'landing.how.step1.title': 'Compare on average price',
  'landing.how.step1.body':
    'See each plan’s PUCT average price side by side, at the usage that matches your home.',
  'landing.how.step2.title': 'Check your address',
  'landing.how.step2.body':
    'We confirm service at your address and identify your local delivery utility (TDU).',
  'landing.how.step3.title': 'Review and sign online',
  'landing.how.step3.body':
    'Complete a credit check, review your contract documents and sign. Your contract is emailed to you.',

  'landing.suppliers.heading': 'Plans from Texas retail providers',
  'landing.suppliers.note': 'Suppliers currently in our catalogue. A listing is not an endorsement.',

  'landing.featured.heading': 'Featured plans',
  'landing.featured.subtitle': 'A few plans to get you started — compare them all on the plans page.',
  'landing.featured.seeAll': 'See all plans',
  'landing.featured.loading': 'Loading plans…',
  'landing.featured.error': 'We couldn’t load plans right now. Please try again.',
  'landing.featured.retry': 'Try again',
  'landing.featured.empty': 'No plans are available right now.',

  'landing.closing.title': 'Ready to switch or start service?',
  'landing.closing.cta': 'Shop plans',

  // ── Meter-first landing (SHOP-2 slice 1) ──────────────────────────────────
  // Method chooser tabs; the tab tokens live in services/funnelMethod.ts.
  'landing.method.tabsAria': 'Choose how to find your meter',
  'landing.method.esiid': 'Shop by ESI ID',
  'landing.method.zip': 'Shop by ZIP',

  // "Shop by ESI ID" sub-flow (SHOP-3) — the shopper types their ESI ID directly.
  'landing.esiid.intro':
    'Enter the 17-digit ESI ID from your electricity bill and we’ll pull up your meter.',
  'landing.esiid.form.label': 'ESI ID',
  'landing.esiid.form.placeholder': 'e.g. 10 4437 2000 1234 512',
  'landing.esiid.form.help': 'Digits only — spaces and hyphens are fine, we’ll tidy them up.',
  'landing.esiid.form.esiid.invalid': 'Enter a valid ESI ID (10–22 digits).',
  'landing.esiid.form.letters': 'An ESI ID is digits only — remove any letters.',
  'landing.esiid.form.submit': 'Find my meter',
  'landing.esiid.form.submitting': 'Looking up your meter…',
  'landing.esiid.notFound.title': "We couldn't find that meter",
  'landing.esiid.notFound.body':
    "No meter matched that ESI ID. Double-check the digits on your bill, or shop by ZIP instead.",
  'landing.esiid.error.title': "We couldn't look up that ESI ID",
  'landing.esiid.error.body': 'Something went wrong. Please try again in a moment.',

  // Shared meter-resolution states (both on-ramps run the ESI ID lookup last).
  'landing.meter.resolving': 'Loading your meter details…',
  'landing.meter.resolveError.title': "We couldn't load your meter details",
  'landing.meter.resolveError.body':
    'We found your address but could not load its meter details. Please try again.',

  // "Shop by ZIP" sub-flow — ZIP entry gates the address picker.
  'landing.zip.zip.label': 'Your ZIP code',
  'landing.zip.zip.placeholder': '5-digit ZIP',
  'landing.zip.zip.help': 'Type a ZIP, or a city name, and pick from the list of areas we serve.',

  // ZIP picker (searchable combobox). Replaced a bare numeric input, which
  // required the shopper to already know a ZIP we serve.
  'landing.zip.picker.listLabel': 'Serviceable ZIP codes',
  'landing.zip.picker.loading': 'Loading service areas…',
  'landing.zip.picker.empty': 'No service area matches that. Check the ZIP, or try a city name.',
  'landing.zip.address.label': 'Service address',
  'landing.zip.address.placeholder': 'Pick your address',
  'landing.zip.address.awaitingZip': 'Enter your ZIP first.',
  'landing.zip.address.loading': 'Looking up serviceable addresses…',
  'landing.zip.address.error': "We couldn't load addresses for that ZIP. Try again in a moment.",
  'landing.zip.address.empty': "No serviceable addresses matched ZIP {{zip}}. Please check the ZIP.",
  'landing.zip.address.help': 'Pick the address that matches your home.',
  'landing.zip.check.cta': 'Check my service',
  'landing.zip.check.pending': 'Checking your address…',

  // Shared meter-summary card — rendered on resolve from EITHER on-ramp.
  // ⚠️ DECLARATIVE, NOT CONVERSATIONAL. "We found your meter" narrates what the app
  // just did; this card's job is to state WHICH service point the enrolment is now
  // bound to, so the customer can check it against their bill before going further.
  // The heading names the record, and the eyebrow labels the section.
  'meter.summary.eyebrow': 'Service point',
  'meter.summary.title': 'Meter details confirmed',
  'meter.summary.field.zip': 'ZIP',
  'meter.summary.field.address': 'Service address',
  'meter.summary.field.tdu': 'Delivery utility (TDU)',
  'meter.summary.field.usage': 'Estimated annual usage',
  'meter.summary.field.customerType': 'Customer type',
  // "Shop" is the one retail-sales word left in the funnel's own controls. "View"
  // states what the button does without implying a sales floor.
  'meter.summary.cta.shopPlans': 'View plans for this meter',
  'meter.summary.cta.startOver': 'Start over',
  'meter.summary.seeAll.aria': 'See all meter details',
  'meter.summary.seeAll.label': 'All details',
  'meter.summary.seeAll.title': 'See all meter details',

  // "See all meter details" dialog (SHOP-3 / SHOP-4) — the full ESIID field set.
  // Values come straight from the resolved MeterDetail (a dummy-data stub today).
  // The ESI ID is DISPLAY-ONLY.
  'meter.detail.title': 'Your meter details',
  'meter.detail.subtitle':
    "Everything we found for the meter at this address. This is your own meter, shown for confirmation.",
  'meter.detail.close': 'Close',
  'meter.detail.field.esiid': 'ESI ID',
  'meter.detail.field.premise': 'Service address',
  'meter.detail.field.zip': 'ZIP',
  'meter.detail.field.tdu': 'Delivery utility (TDU)',
  'meter.detail.field.enrollmentType': 'Enrollment type',
  'meter.detail.field.customerType': 'Customer type',
  'meter.detail.field.usage': 'Estimated annual usage',
  'meter.detail.field.loadZone': 'ERCOT load zone',
  'meter.detail.field.smartMeter': 'Meter type',
  'meter.detail.field.serviceStatus': 'Service status',

  // Shared meter field VALUES (used by both the card and the dialog).
  // ⚠️ E-SF19 P2 — the address is an EXAMPLE, not this meter's premise. Shown under
  // "Meter details confirmed", so it has to say so where it is read. Only ever
  // rendered when the server sets `MeterDetail.addressIsPlaceholder`.
  'meter.field.address.placeholder': 'Example address — we don’t have this meter’s street on file yet',
  'meter.field.usage.value': '{{kwh}} kWh / year',
  'meter.field.customerType.RESIDENTIAL': 'Residential',
  'meter.field.customerType.BUSINESS': 'Business',
  'meter.field.loadZone.LZ_NORTH': 'North (LZ_NORTH)',
  'meter.field.loadZone.LZ_HOUSTON': 'Houston (LZ_HOUSTON)',
  'meter.field.loadZone.LZ_SOUTH': 'South (LZ_SOUTH)',
  'meter.field.loadZone.LZ_WEST': 'West (LZ_WEST)',
  'meter.field.meterType.smart': 'Smart meter (AMS)',
  'meter.field.meterType.NON_AMS': 'Legacy meter (non-AMS)',
  'meter.field.meterType.IDR': 'Interval meter (IDR)',
  'meter.field.meterType.AMS': 'Smart meter (AMS)',
  'meter.field.serviceStatus.ACTIVE': 'Active',
  'meter.field.serviceStatus.INACTIVE': 'Inactive',

  // ── Consumption selector (SHOP-4 addition) ────────────────────────────────
  // Monthly kWh. Tiers are pre-selected from the meter's estimated usage; the
  // ranges are baked into the labels (the numbers live in services/consumption.ts).
  'consumption.heading': 'How much electricity do you use?',
  'consumption.subtitle': "We pre-filled this from your meter — adjust it to price plans at your usage.",
  'consumption.tier.low': 'Low usage (0–500 kWh/mo)',
  'consumption.tier.medium': 'Medium usage (500–1,000 kWh/mo)',
  'consumption.tier.high': 'High usage (1,000–2,000 kWh/mo)',
  'consumption.tier.custom': 'Custom',
  // Tile presentation: the tier word carries the choice, the range explains it.
  'consumption.tier.low.short': 'Low',
  'consumption.tier.low.range': '0–500 kWh/mo',
  'consumption.tier.medium.short': 'Medium',
  'consumption.tier.medium.range': '500–1,000 kWh/mo',
  'consumption.tier.high.short': 'High',
  'consumption.tier.high.range': '1,000–2,000 kWh/mo',
  'consumption.tier.custom.short': 'Custom',
  'consumption.tier.custom.range': 'Set your own',
  'consumption.custom.label': 'Your monthly usage (kWh)',
  'consumption.custom.placeholder': 'e.g. 1200',
  'consumption.custom.help': 'Enter your typical monthly usage in kWh.',
  'consumption.custom.invalid': 'Enter a whole number of kWh greater than zero.',
  'consumption.cta.hint': 'Choose your usage to see plans.',

  // ── SHOP-5A — meter-filtered tariff shelf ─────────────────────────────────
  'meterShelf.title': 'Plans for your meter',
  // The context line renders as prose + PILLS (utility, usage) — see the page.
  'meterShelf.context.showing': 'Showing plans from',
  'meterShelf.context.pricedAt': 'priced at',
  'meterShelf.context.usage': '{{kwh}} kWh/mo',
  'meterShelf.loading': 'Loading plans…',
  'meterShelf.error.title': "We couldn't load plans",
  'meterShelf.error.body': 'Something went wrong reaching the plan catalogue. Please try again.',
  'meterShelf.retry': 'Try again',
  'meterShelf.empty.title': 'No plans for your area yet',
  'meterShelf.empty.body': "We don't have plans for {{tdu}} right now. Please check back soon.",
  'meterShelf.business.title': 'No plans available for your meter yet',
  'meterShelf.business.body':
    'Your meter is a business (commercial) premise. We only offer residential plans today — business plans are coming soon.',
  'meterShelf.back': 'Start over',
  // Shelf ordering. The default is price-ascending (planOrdering.ts); these labels
  // name the ordering in the customer's terms, and the price ones say WHICH price —
  // the §25.475 average at the usage they chose, which is the figure on the card.
  'meterShelf.sort.label': 'Sort by',
  'meterShelf.sort.price-asc': 'Average price — lowest first',
  'meterShelf.sort.price-desc': 'Average price — highest first',
  'meterShelf.sort.term-asc': 'Contract length — shortest first',

  // ── Plan compare (E-SF11) ─────────────────────────────────────────────────
  // Compliance: the compare modal headlines the §25.475 AVERAGE price (row
  // 'compare.row.avgPrice'); the raw energy charge is shown but clearly labelled
  // 'compare.row.energyCharge', never as "the price".
  'card.compare': 'Compare',
  // ── Card flip + quick-view (the two-tap shelf) ─────────────────────────
  'card.info.aria': "About this plan's price",
  'card.aboutPrice': 'About this price',
  'card.back': 'Back to plan',
  'card.quickview.close': 'Close',
  'card.quickview.buy': 'Buy this plan',
  'card.quickview.docsNote':
    "You'll see this plan's Electricity Facts Label, Terms of Service and Your Rights as a Customer before anything is signed.",
  'compare.tray.count': '{{count}} selected to compare',
  'compare.tray.clear': 'Clear',
  'compare.tray.open': 'Compare',
  'compare.modal.title': 'Compare plans',
  'compare.row.avgPrice': 'Average price',
  'compare.row.energyCharge': 'Energy charge',
  'compare.row.type': 'Plan type',
  'compare.row.term': 'Term',
  'compare.row.etf': 'Early termination fee',
  'compare.row.renewable': 'Renewable content',
  'compare.row.tdsp': 'Delivery utility (TDU)',
  'compare.centsPerKwh': '{{value}} ¢/kWh',
  'compare.noEtf': 'No cancel fee',
  'compare.remove.aria': 'Remove {{plan}} from the comparison',
  'compare.remove.title': 'Remove',

  // ── Optional ZIP filter on the shelf (E-SF10) ─────────────────────────────
  // The ZIP→TDU map is demo reference data; the plans it filters are the real
  // single-source catalogue. Copy is factual — it filters, it does not promise.
  'zip.filter.label': 'See plans for your area',
  'zip.filter.placeholder': 'Enter your 5-digit ZIP',
  'zip.filter.apply': 'Show my area',
  'zip.filter.clear': 'Clear',
  'zip.filter.invalid': 'Enter a valid 5-digit ZIP code.',
  'zip.filter.active': 'Showing {{tdu}} plans for ZIP {{zip}}',
  'zip.filter.notServed':
    "We couldn't match ZIP {{zip}} to a Texas delivery area yet — showing all plans.",
  'zip.filter.help': 'We use your ZIP to show plans from your local delivery utility (TDU).',

  // ── ZIP-first entry gate (E-SF10) ─────────────────────────────────────────
  // First step: ZIP (must resolve to a served TDU) + home type (drives the
  // headline consumption). Apostrophe-free copy — these are single-quoted strings.
  'start.title': 'Find plans for your home',
  'start.subtitle': 'Enter your ZIP and home type to see plans for your area.',
  'start.zip.label': 'Your ZIP code',
  'start.zip.placeholder': '5-digit ZIP',
  'start.zip.invalid': 'Enter a valid 5-digit ZIP code.',
  'start.zip.notServed': 'We do not serve that area yet — try a Texas ZIP we cover.',
  'start.home.label': 'What type of home?',
  'start.home.required': 'Please choose your home type.',
  'start.submit': 'Show my plans',
  'shelf.areaContext': 'Showing {{tdu}} plans for ZIP {{zip}} · average price at {{kwh}} kWh/mo',
  'shelf.changeArea': 'Change area',
  'address.form.zip.fromGate': 'From your ZIP code',
  'detail.continueToAddress': 'Check service at your address',
  'sign.confirm.downloadPdf': 'Download your signed agreement (PDF)',
  'sign.confirm.downloadPreparing': 'Preparing your agreement…',
  // ⚠️ A SEPARATE KEY FROM `downloadPreparing`, which is the download button's own
  // in-flight label. This one means the CONTRACT does not exist yet; that one means
  // the customer's click is being served. Sharing a string made one test assert the
  // other state's label.
  'sign.confirm.contractPreparing': 'Preparing your signed contract…',
  // Shown only once the wait has TIMED OUT. The screen stops claiming the document
  // is moments away — past the worker's own stuck threshold that would be a
  // comforting fiction — and points at the channel that survives a closed tab.
  'sign.confirm.downloadStillPreparing':
    'This is taking longer than usual. We will email your signed contract as soon as it is ready — you do not need to wait here.',
  'sign.confirm.downloadPreparingHint': 'Your download will start automatically.',
  // Deliberately says "first time": measured 2026-08-19, a contract's first
  // download renders the document on demand and took 121s, while every later
  // download of the same contract took 0.8s. Silence for two minutes reads as
  // a broken button, so we name the wait instead of hiding it.
  'sign.confirm.downloadSlow':
    'Still preparing — the first download of a new agreement can take a minute or two.',
  // NOT an error title. The document is not lost and nothing needs re-signing;
  // it is still being produced. See SignedAgreementDownload.tsx.
  'sign.confirm.downloadNotReady.title': 'Your agreement is still being prepared',
  'sign.confirm.downloadNotReady.body':
    'It is safely signed and saved — the file just is not finished yet. Please try again in a minute.',
  'sign.confirm.downloadRetry': 'Try the download again',
  'zip.filter.empty.title': 'No plans for this area yet',
  'zip.filter.empty.body': 'We don’t have plans for that delivery area right now. Clear the ZIP to see all plans.',
};
